/**
 * ManifestoVideo — the manifesto's right-column media. Client, 2026-09-19:
 * 0:07-0:37 (30s) of the April '26 ceremony reel (gm-april26-reel-ceremony), a
 * portrait 9:16 source, so the card is now a 3:4 portrait box (see .gm-video
 * in components.css) and the video FILLS it (object-fit: cover, centred) with
 * only a sliver cropped top and bottom.
 *
 * Encoded for THIS card, not the phone hero: the box is at most 470px wide,
 * so 576x1024 H.264 at ~650 kbps (~2.4 MB for 30s, faststart, 2s keyframes)
 * plus a VP9 WebM for Chromium builds without H.264. Same 0:07-0:37 cut as
 * the Corporate Events phone hero, which keeps its own 720x1280 file.
 *
 * Autoplays muted, loops, inline (iOS). Paused while off-screen so it never
 * competes with the Creed glide for compositor time; the poster is the
 * reduced-motion / no-autoplay fallback.
 */
import { useEffect, useRef } from "react";
import { LOADER_DONE } from "./Loader";

export const MANIFESTO_VIDEO = "/video/ceremony-card.mp4";
export const MANIFESTO_VIDEO_WEBM = "/video/ceremony-card.webm";
export const MANIFESTO_POSTER = "/video/ceremony-card.jpg";

export default function ManifestoVideo() {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    // React sets the `muted` PROPERTY only; iOS Safari's autoplay policy
    // wants the attribute in the DOM too, so set both explicitly.
    el.muted = true;
    el.defaultMuted = true;
    el.setAttribute("muted", "");
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return undefined;
    if (!("IntersectionObserver" in window)) {
      el.play?.().catch(() => {});
      return undefined;
    }
    // Below the fold. Nothing is fetched while the page loader is up (the
    // hero film must have the connection to itself) - but the MOMENT the site
    // is revealed the card starts buffering, so by the time the reader has
    // scrolled one screen the first seconds are already on disk. Playback
    // itself still follows the viewport: play near it, pause away from it
    // (client, 2026-09-19: "the video below takes time to load").
    let io = null;
    const arm = () => {
      if (io) return;
      if (el.preload !== "auto") { el.preload = "auto"; el.load?.(); }
      io = new IntersectionObserver(
        (entries) => {
          const on = !!entries[0]?.isIntersecting;
          if (on) el.play?.().catch(() => {});
          else el.pause?.();
        },
        { threshold: 0, rootMargin: "400px 0px" }
      );
      io.observe(el);
    };
    if (window.__gmLoaderDone) arm();
    else window.addEventListener(LOADER_DONE, arm, { once: true });
    return () => { window.removeEventListener(LOADER_DONE, arm); io?.disconnect(); };
  }, []);

  return (
    <div className="gm-film gm-video" data-testid="manifesto-video-card">
      <video
        ref={ref}
        className="gm-video__el"
        poster={MANIFESTO_POSTER}
        muted
        loop
        playsInline
        preload="none"
        aria-label="Grid Masters podium ceremony"
        data-testid="manifesto-video"
      >
        <source src={MANIFESTO_VIDEO} type="video/mp4" />
        <source src={MANIFESTO_VIDEO_WEBM} type="video/webm" />
      </video>
    </div>
  );
}
